import { useState, useRef, useCallback, useEffect } from 'react'
import { Upload, X, Image as ImageIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { uploadFile, validateImageFile, getDownloadUrl } from '@/services/storage'

interface ImageUploadProps {
  value?: string
  onChange: (url: string) => void
  onUploadStart?: () => void
  onUploadComplete?: () => void
  onUploadError?: (error: string) => void
  className?: string
  accept?: string
  maxSizeMB?: number
  label?: string
  placeholder?: string
  previewUrl?: string // For displaying existing images without affecting the form value
}

export default function ImageUpload({
  value,
  onChange,
  onUploadStart,
  onUploadComplete,
  onUploadError,
  className,
  accept = 'image/jpeg,image/png,image/webp,image/gif',
  maxSizeMB = 10,
  label,
  placeholder = 'Click to upload or drag and drop',
  previewUrl
}: ImageUploadProps) {
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [preview, setPreview] = useState<string | null>(null)
  const [dragActive, setDragActive] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [displayUrl, setDisplayUrl] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

  // Load display URL for file IDs or use previewUrl
  useEffect(() => {
    // If there's an active preview from upload, don't override it
    if (preview) {
      return;
    }

    if (value && uuidRegex.test(value)) {
      // It's a file ID, fetch the display URL
      getDownloadUrl(value).then(url => {
        if (url) {
          setDisplayUrl(url)
        }
      }).catch(() => {
        // Failed to get URL, clear display
        setDisplayUrl(null)
      })
    } else if (value && !uuidRegex.test(value)) {
      // It's a regular URL
      setDisplayUrl(value)
    } else if (previewUrl && !value) {
      // Use previewUrl when form value is empty (e.g., presigned URLs cleared from form)
      setDisplayUrl(previewUrl)
    } else {
      setDisplayUrl(null)
    }
  }, [value, preview, previewUrl])

  const handleFile = useCallback(
    async (file: File) => {
      // Reset error state
      setError(null)
      
      // Validate file
      const validation = validateImageFile(file, maxSizeMB)
      if (!validation.isValid) {
        const errorMsg = validation.errors.join(', ')
        setError(errorMsg)
        onUploadError?.(errorMsg)
        return
      }

      // Create preview
      const reader = new FileReader()
      reader.onloadend = () => {
        setPreview(reader.result as string)
      }
      reader.readAsDataURL(file)

      // Start upload
      setIsUploading(true)
      setUploadProgress(0)
      onUploadStart?.()

      try {
        // Simulate progress for backend upload
        const progressInterval = setInterval(() => {
          setUploadProgress((prev) => {
            if (prev >= 85) {
              clearInterval(progressInterval)
              return 85
            }
            return prev + 15
          })
        }, 300)

        const result = await uploadFile(file)

        clearInterval(progressInterval)
        setUploadProgress(100)

        if (result.url) {
          onChange(result.url)
          onUploadComplete?.()
        } else {
          throw new Error('No URL returned from upload')
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Upload failed'
        setError(errorMessage)
        onUploadError?.(errorMessage)
        setPreview(null)
      } finally {
        setIsUploading(false)
        setUploadProgress(0)
      }
    },
    [onChange, onUploadStart, onUploadComplete, onUploadError, maxSizeMB]
  )

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      handleFile(file)
    }
  }

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    const file = e.dataTransfer.files?.[0]
    if (file && file.type.startsWith('image/')) {
      handleFile(file)
    } else {
      setError('Please drop an image file')
    }
  }

  const handleRemove = () => {
    setPreview(null)
    setDisplayUrl(null)
    onChange('')
    setError(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleUrlInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value)
  }

  return (
    <div className={cn('w-full', className)}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      
      <div className="space-y-2">
        {/* File Upload Area */}
        <div
          className={cn(
            'relative border-2 border-dashed rounded-lg p-4 transition-colors',
            dragActive
              ? 'border-primary bg-primary/5'
              : 'border-gray-300 hover:border-gray-400',
            isUploading && 'pointer-events-none opacity-60'
          )}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={accept}
            onChange={handleFileInput}
            className="hidden"
            disabled={isUploading}
          />

          {preview ? (
            <div className="relative">
              <img
                src={preview}
                alt="Preview"
                className="w-32 h-32 mx-auto rounded-lg object-cover"
              />
              {!isUploading && (
                <button
                  type="button"
                  className="absolute top-0 right-0 transform translate-x-1/2 -translate-y-1/2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  onClick={handleRemove}
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          ) : displayUrl && !preview ? (
            <div className="relative">
              <img
                src={displayUrl}
                alt="Current"
                className="w-32 h-32 mx-auto rounded-lg object-cover"
                onError={() => setDisplayUrl(null)}
              />
              <button
                type="button"
                className="absolute top-0 right-0 transform translate-x-1/2 -translate-y-1/2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                onClick={handleRemove}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div
              className="flex flex-col items-center justify-center py-4 cursor-pointer"
              onClick={() => !isUploading && fileInputRef.current?.click()}
            >
              <Upload className="w-8 h-8 text-gray-400 mb-3" />
              <p className="text-sm text-gray-600 mb-1">{placeholder}</p>
              <p className="text-xs text-gray-500">
                PNG, JPG, GIF or WebP (max {maxSizeMB}MB)
              </p>
            </div>
          )}

          {/* Upload Progress */}
          {isUploading && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">Uploading...</span>
                <span className="text-sm text-gray-600">{uploadProgress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}
        </div>

        {/* URL Input Alternative */}
        <div className="relative">
          <input
            type="url"
            value={value || ''}
            onChange={handleUrlInput}
            placeholder="Or paste an image URL"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
            disabled={isUploading || !!preview}
          />
          {value && !preview && (
            <ImageIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="text-sm text-red-600 mt-1">{error}</div>
        )}
      </div>
    </div>
  )
}